import { TWO_FACTOR_CODE_LENGTH } from '../../domain/two-factor';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChildren
} from '@angular/core';
import type { ValidationErrors } from '@angular/forms';
import type { I18nService } from '@ppl/i18n';
import type { TAbstractControl } from '@ppl/ui/form';
import { PplInputDirective } from '@ppl/ui/input';
import { FormValueControl, getFormControlProvider } from '@ppl/utils/form.utils';
import { KEYCODE_ENTER } from '@ppl/utils/keycode.utils';
import { MemoizeLast } from '@ppl/utils/memoize.utils';

@Component({
    selector: 'ppl-two-factor-verification-code',
    templateUrl: './two-factor-verification-code.component.html',
    styleUrls: ['./two-factor-verification-code.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        getFormControlProvider(() => TwoFactorVerificationCodeComponent)
    ],
    standalone: false
})
@FormValueControl()
export class TwoFactorVerificationCodeComponent {

  @Input() value: TwoFactorVerificationCodeNum[];

  @Output() valueChange = new EventEmitter<string[]>();
  @Output() submit = new EventEmitter();

  @ViewChildren(PplInputDirective, { read: ElementRef }) inputs: QueryList<ElementRef<HTMLInputElement>>;

  @MemoizeLast<TwoFactorVerificationCodeComponent>(['value'])
  get numbers(): TwoFactorVerificationCodeNum[] {
    if (this.value && this.value.length) {
      return this.createNumbers(this.value);
    } else {
      return Array.from({ length: TWO_FACTOR_CODE_LENGTH }, () => null);
    }
  }

  constructor(
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const data = event.clipboardData.getData('Text');
    const text = data.slice(0, TWO_FACTOR_CODE_LENGTH);
    if (this.isNumber(text)) {
      const value = this.createNumbers(text);
      this.valueChange.emit(value);
    }
  }

  onInput(event: InputEvent, index: number) {
    const number = event.data;
    const newValue = this.numbers.map((oldNum, idx) => idx === index ? number : oldNum);
    const nextInputElementIndex = (index + 1) % this.inputs.length;
    if (number && nextInputElementIndex !== 0) {
      const inputElement = this.inputs.get(nextInputElementIndex).nativeElement;
      inputElement.focus();
      inputElement.select();
    }
    this.valueChange.emit(newValue);
  }

  onKeyUp(event: KeyboardEvent) {
    if (event.keyCode === KEYCODE_ENTER) {
      this.submit.emit();
    }
  }

  createNumbers(value: TwoFactorVerificationCodeNum[] | string): TwoFactorVerificationCodeNum[] {
    return Array.from({ length: TWO_FACTOR_CODE_LENGTH }, (_, index) => {
      const num = value[index];
      return num ? num : null;
    });
  }

  isNumber(text: string) {
    return /^\d+$/.test(text);
  }

  trackByIndex(index: number) {
    return index;
  }
}

type TwoFactorVerificationCodeNum = string | null;

export function verificationCodeValidator(i18nService: I18nService) {
  return (control: TAbstractControl<TwoFactorVerificationCodeNum[]>): ValidationErrors | null => {
    const numbers = control.value;
    if (numbers) {
      return numbers.length !== TWO_FACTOR_CODE_LENGTH || !numbers.every(number => Boolean(number))
        ? { custom: i18nService.translate('Incomplete_verification_code') }
        : null;
    } else {
      return null;
    }
  };
}
